<template>
  <ul
    v-bind="{
      ...(mounted && {
        class: 'categories-list grid-y',
      }),
    }"
  >
    <template v-for="(category, index) in normalizedData">
      <li v-if="category.isVisible" :key="`${category.id}-${index}`">
        <component
          :is="category.url ? Link : 'button'"
          v-if="category.isButton"
          v-bind="{
            ...(mounted && {
              class: `category-button grid-x align-middle align-justify undecorated-link ${isRedFob(category) ? 'red-fob' : ''}`,
              'data-flyout-id': category.id,
              'aria-expanded': stack.findIndex((i) => i.id === category.id) >= 0,
              tabindex: '0',
            }),
          }"
          :href="category.url"
          :data-id="category.id"
          :data-tracking="trackingObjToString(category.tracking)"
          :data-url="category.url"
          :data-cat-splash="category.isCatSplash || undefined"
          :style="{ color: category.fontColor }"
          @keydown.enter.prevent="handleEnterKeydown(category, index, true)"
          @click.prevent="handleCategoryClick(category, index, true)"
        >
          <span
            v-bind="{
              ...(mounted && {
                class: 'category-text',
              }),
            }"
          >
            <span>{{ parseCategoryText(category.text) }}</span>
            <ToysIcon v-if="mounted && isMcom && isToysCategory({ id: category.id, text: category.text })" img-class="margin-left-xxs" big />
          </span>
          <i
            v-bind="{
              ...(mounted && {
                class: 'chevron-small-right-black chevron-small-right',
              }),
            }"
          ></i>
        </component>
        <Link
          v-else
          v-bind="{
            ...(mounted && {
              class: `category-link undecorated-link grid-x align-middle align-justify ${isRedFob(category) ? 'red-fob' : ''}`,
              tabindex: '0',
            }),
            href: category.url,
          }"
          :data-id="category.id"
          :data-tracking="trackingObjToString(category.tracking)"
          :data-skip="category.isParentCategory || undefined"
          :style="{ color: category.fontColor }"
          @keydown.enter="handleEnterKeydown(category, index, false)"
          @click="handleCategoryClick(category, index, false)"
        >
          <span
            v-bind="{
              ...(mounted && {
                class: 'category-text',
              }),
            }"
            >{{ category.text }}</span
          >
        </Link>
      </li>
    </template>
  </ul>
</template>

<script setup>
import { computed } from 'vue';
import { useMounted } from '@vueuse/core';

import Link from '../common/Link';
import { toPascalCase } from '#shared/utils/StringUtil';
import { tagNavLinkClick } from '#shared/features/analytics/navigationTags';
import { ExpiresStorageProxy } from '~/utils/storage/expireStorageProxy';
import { isRedFob } from '~/utils/fob';
import { useMobileNav } from '~/composables/useMobileNav';
import { useHamburgerMenu } from '~/composables/useHamburgerMenu';
import useSignOutClick from '~/composables/useSignOutClick';
import useSignInClick from '~/composables/useSignInClick';
import { trackingObjToString } from '~/utils/restoreStateFromDom';
import isToysCategory from '~/utils/isToysCategory';
import focusAfterTransition from '~/utils/focusAfterTransition';
import ToysIcon from '~/components/dumb/ToysIcon.mcom';

const props = defineProps({
  data: {
    /** @type import('vue').PropType<MobileFlyoutElement[]> */
    type: Object,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  categoryId: {
    type: String,
    default: '',
  },
  level: {
    type: String,
    default: 'l1',
  },
});

const emits = defineEmits(['onSelect']);
const mounted = useMounted();

const envProps = useEnvProps();
const pageData = usePageData();
const navViewType = useNavViewType();

const { menu } = useHamburgerMenu();
const { analytics, stack, lastStackItem } = useMobileNav();

const isMcom = envProps.isMcom;
const { context } = pageData.navigation;

const viewType = computed(() => navViewType.viewType);

const normalizedData = computed(() =>
  props.data.map((item) => {
    const _isMobileFlyoutContainer = isMobileFlyoutContainer(item);
    const currentLevel = mounted.value && lastStackItem()?.id === props.categoryId ? stack.value.length + 1 : 1;
    let isVisible;
    let isButton;

    if (currentLevel < 3) {
      isButton = _isMobileFlyoutContainer;
      isVisible = item.url || _isMobileFlyoutContainer;
    } else if (currentLevel === 3) {
      // L3: show only links with url and buttons for browse-hide categories
      isButton = !item.url && _isMobileFlyoutContainer;
      isVisible = item.url || isButton;
    } else {
      // L4: show only links with url
      isButton = false;
      isVisible = item.url;
    }

    return {
      ...item,
      isVisible,
      isButton,
    };
  }),
);

const navContentStackLength = () => analytics.getCol() + 1;

/**
 *
 * @param {MobileFlyoutElement} cat
 * @returns {Boolean}
 */
function hasChildren(cat) {
  return Boolean(menu.value[cat.id]?.children?.length);
}

/**
 *
 * @param {MobileFlyoutElement} flyout
 * @returns {flyout is MobileFlyoutContainer}
 */
function isMobileFlyoutContainer(flyout) {
  return hasChildren(flyout) && !flyout.isParentCategory;
}

const parseCategoryText = (s) => (isMcom ? s : toPascalCase(s));

/**
 * @param category
 * @param index
 * @param isButton
 */
async function handleCategoryClick(category, index, isButton) {
  const levelsData = [...stack.value, { title: category.text }].reduce((acc, cur, i) => {
    if (i) {
      acc[`subCategory${i}Name`] = cur.title;
    } else {
      acc.topCategoryName = cur.title;
    }

    return acc;
  }, {});

  const params = {
    context,
    pos: index + 1,
    col: navContentStackLength(),
    categoryId: category.id,
    analyticsCategoryId: category.id,
    clickableElement: category.text,
    tracking: category.tracking,
    title: category.text,
    url: category.url,
    level: props.level,
    ...levelsData,
  };

  tagNavLinkClick(isMcom, params, viewType.value, { isShopAll: true });

  if (isButton) {
    emits('onSelect', params);
  }

  const { isSignIn, isSignOut, burstCache } = category;

  if (burstCache) {
    ExpiresStorageProxy(localStorage).clear();
  }

  if (isSignIn) {
    useSignInClick();
  }

  if (isSignOut) {
    useSignOutClick(isMcom);
  }
}

async function handleEnterKeydown(category, index, isButton) {
  await handleCategoryClick(category, index, isButton);
  if (isButton) {
    focusAfterTransition({
      query: `a[data-testid="shop-all-button"], div[data-id='${category.id}'] a`,
      panelClass: '.transition-panel',
    });
  }
}
</script>

<style scoped lang="scss">
.category-text {
  display: flex;
  width: calc(100% - 24px - 16px);
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  line-height: 1.5rem; // force line height to match icon (no visual change, but makes testing easier)

  :deep(img) {
    vertical-align: top;
  }
}

.category-button {
  cursor: pointer;
  width: 100%;
}
</style>
